import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import ContentLoader from 'react-content-loader';

import './donutChart.scss';

const MAX_RADIUS = 150;

const TEXT_MAX_WIDTH = 40;
const TEXT_PADDING = 10;
const TEXT_HEIGHT = 16;
const TEXT_MARGIN = 2;

const DonutChart = ({ vendorName, products, width, height, focusProduct, onFocusChange }) => {
    const rearrangeProducts = useMemo(() => {
        // Sometime, total percent not 100%, we need to rearrange them
        if (!products) {
            return;
        }

        // Total percent
        const total = products.reduce((entire, product) => entire + product.devicePercentage, 0.0);

        // Process data
        let sum = 0.0;
        return products.filter((product) => product.devicePercentage > 0.0).map((product) => {
            const percent = parseFloat(product.devicePercentage) / total;

            const percentStart = sum;
            const percentEnd = sum + percent;
            const percentCenter = sum + percent / 2.0;

            const processed = {
                ...product,
                percent,
                percentStart,
                percentEnd,
                percentCenter,
                isLarge: product.devicePercentage > 0.5,
            };

            sum = percentEnd;
            return processed;
        });
    }, [products]);

    const pieValueTexts = useMemo(() => {
        if (!rearrangeProducts) {
            return;
        }

        return {
            topRight: rearrangeProducts.filter((product) => product.percentCenter < 0.25),
            bottomRight: rearrangeProducts.filter((product) => product.percentCenter >= 0.25 && product.percentCenter < 0.5),
            bottomLeft: rearrangeProducts.filter((product) => product.percentCenter >= 0.5 && product.percentCenter < 0.75),
            topLeft: rearrangeProducts.filter((product) => product.percentCenter >= 0.75),
        };
    }, [rearrangeProducts]);

    const centerPoint = useMemo(() => {
        if (!pieValueTexts) {
            return [width / 2, height / 2, MAX_RADIUS];
        }

        const topTextsCount = Math.max(...[pieValueTexts.topRight.length, pieValueTexts.topLeft.length]);
        const bottomTextsCount = Math.max(...[pieValueTexts.bottomRight.length, pieValueTexts.bottomLeft.length]);

        const topPadding = topTextsCount * (TEXT_HEIGHT + TEXT_MARGIN) + TEXT_PADDING * 2;
        const bottomPadding = bottomTextsCount * (TEXT_HEIGHT + TEXT_MARGIN) + TEXT_PADDING * 2;

        const horizontalPadding = TEXT_MAX_WIDTH + TEXT_PADDING;

        const availablePieWidth = width - horizontalPadding * 2;
        const availablePieHeight = height - topPadding - bottomPadding;

        const minSize = Math.min(availablePieWidth, availablePieHeight);
        const range = (minSize / 2) > MAX_RADIUS ? MAX_RADIUS : (minSize / 2);

        const centerVertical = (height - (range * 2 + topPadding + bottomPadding)) / 2 + topPadding + range;

        return [width / 2, centerVertical, range];
    }, [width, height, pieValueTexts]);

    const radius = useMemo(() => centerPoint[2], [centerPoint]);

    const getCoordinates = useCallback((percent, padding = 0) => {
        const r = (radius + padding);
        const x = Math.sin(2 * Math.PI * percent) * r + radius;
        const y = radius - Math.cos(2 * Math.PI * percent) * r;

        const cx = Math.sin(2 * Math.PI * percent) * r / 2 + radius;
        const cy = radius - Math.cos(2 * Math.PI * percent) * r / 2;

        const translateX = Math.sin(2 * Math.PI * percent) * padding;
        const translateY = Math.cos(2 * Math.PI * percent) * padding * -1;

        return [x, y, cx, cy, translateX, translateY];
    }, [radius]);

    const processedProducts = useMemo(() => {
        // Process data
        return rearrangeProducts?.map((product) => ({
            ...product,
            startCoordinates: getCoordinates(product.percentStart),
            endCoordinates: getCoordinates(product.percentEnd),
            centerCoordinates: getCoordinates(product.percentCenter, 5),
        }));
    }, [getCoordinates, rearrangeProducts]);


    const pie = useMemo(() => {
        if (!processedProducts) {
            return <ContentLoader
                height={'100%'}
                width={'100%'}
                speed={1}
                viewBox={'0 0 100 100'}
                className='chartLoader'
            >
                <circle cx='50%' cy='50%' r='120'/>
            </ContentLoader>;
        }

        const pieRadius = radius >= 0 ? radius : 0;

        const isProductLarge = (product) => {
            return product?.isLarge ? 1 : 0;
        };

        const calculateTransform = (focusProduct, product) => {
            return focusProduct?.name === product?.name ? `translate(${product.centerCoordinates[4]} ${product.centerCoordinates[5]})` : undefined;
        };

        return <g transform={`translate(${centerPoint[0] - radius} ${centerPoint[1] - radius})`}>
            {processedProducts.map((product, index) => product.devicePercentage !== 1 ? <path
                className='piePath'
                key={index}
                d={
                    `M ${product.startCoordinates[0]} ${product.startCoordinates[1]} ` +
                    `A ${radius} ${radius} 0 ${isProductLarge(product)} 1 ${product.endCoordinates[0]} ${product.endCoordinates[1]} ` +
                    `L ${product.endCoordinates[2]} ${product.endCoordinates[3]} ` +
                    `A ${radius / 2} ${radius / 2} 0 ${isProductLarge(product)} 0 ${product.startCoordinates[2]} ${product.startCoordinates[3]} ` +
                    'Z'
                }
                fill={product.color}
                transform={calculateTransform(focusProduct, product)}
                onMouseEnter={() => onFocusChange(product)}
                onMouseLeave={() => onFocusChange(undefined)}
            /> : <circle
                key={index}
                fill={product.color} cx={pieRadius} cy={pieRadius} r={pieRadius}
                onMouseEnter={() => onFocusChange(product)}
                onMouseLeave={() => onFocusChange(undefined)}
            />)}
            <circle className='centerCircle' cx={pieRadius} cy={pieRadius} r={pieRadius / 2} fill={'white'}/>
            <foreignObject width={pieRadius} height={pieRadius} x={pieRadius / 2} y={pieRadius / 2}>
                <div xmlns='http://www.w3.org/1999/xhtml' className='circleText'>
                    <span>{focusProduct?.name || vendorName}</span>
                </div>
            </foreignObject>
        </g>;
    }, [processedProducts, radius, centerPoint, vendorName, focusProduct]);

    const pointers = useMemo(() => {
        if (!pieValueTexts) {
            return;
        }

        const buildText = (items, isBottom, isLeft) => {
            const xSign = isLeft ? -1 : 1;
            const ySign = isBottom ? 1 : -1;
            const x = centerPoint[0] + (radius + TEXT_PADDING) * xSign;
            const y = centerPoint[1] + (radius + TEXT_PADDING) * ySign;

            return items.map((product, index) => {
                let centerCoordinates = getCoordinates(product.percentCenter, 5);
                centerCoordinates[0] = centerCoordinates[0] + (centerPoint[0] - radius);
                centerCoordinates[1] = centerCoordinates[1] + (centerPoint[1] - radius);
                const processedY = y + index * (TEXT_HEIGHT + TEXT_MARGIN) * xSign;
                return <g key={index}>
                    <text x={x + TEXT_PADDING * xSign / 2} y={processedY + TEXT_HEIGHT / 4} className='pointerValue' textAnchor={isLeft ? 'end' : 'start'}>{Math.floor(product.devicePercentage * 100)}%</text>
                    <path d={`M ${centerCoordinates[0]} ${centerCoordinates[1]} L ${centerCoordinates[0]} ${processedY} L ${x} ${processedY}`} fill='none' strokeWidth={.5} stroke={'#273454'}/>
                </g>;
            });
        };

        return <>
            {/*Top right texts*/}
            {buildText(pieValueTexts.topRight, false, false)}

            {/*Bottom right texts*/}
            {buildText(pieValueTexts.bottomRight, true, false)}

            {/*Bottom left texts*/}
            {buildText(pieValueTexts.bottomLeft, true, true)}

            {/*Top Left texts*/}
            {buildText(pieValueTexts.topLeft, false, true)}
        </>;
    }, [pieValueTexts, radius, centerPoint, getCoordinates]);

    return <svg className='donutChart' viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
        {pie}
        {pointers}
    </svg>;
};

DonutChart.propTypes = {
    vendorName: PropTypes.string,
    products: PropTypes.array,
    width: PropTypes.number,
    height: PropTypes.number,
    focusProduct: PropTypes.object,
    onFocusChange: PropTypes.func
};

export default DonutChart;
